import React, { useState, useEffect, useCallback, useRef } from "react"
import { useTranslation } from "react-i18next"
import {
  Box,
  Stack,
  Typography,
  Avatar,
  Button,
  FormControl,
  Select,
  MenuItem,
  CircularProgress,
  IconButton,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
} from "@mui/material"
import PaletteIcon from "@mui/icons-material/Palette"
import CloudUploadIcon from "@mui/icons-material/CloudUpload"
import DeleteIcon from "@mui/icons-material/Delete"
import config from "../../config/app.config"
import { fetchProductCategories, fetchCategoryOptions } from "helpers/productCategoryApi"
import { COLOR_FAMILIES, getColorFamily } from "constants/catalogData"

const API_BASE = `${config.API_URL}files/${config.APP_NAME}/category-swatches`
const API_KEY = config.APP_API_KEY

const getHeaders = () => {
  const authUser = JSON.parse(localStorage.getItem("authUser"))
  const token = authUser?.session_token || ""
  return {
    Accept: "application/json",
    Authorization: token ? `Bearer ${token}` : "",
    "X-Flexflow-Api-Key": API_KEY,
  }
}

const SwatchesTab = ({ onSnackbar }) => {
  const { t } = useTranslation()
  const [models, setModels] = useState([])
  const [modelsLoading, setModelsLoading] = useState(true)
  const [selectedPG, setSelectedPG] = useState("")
  const [swatches, setSwatches] = useState([])
  const [loading, setLoading] = useState(false)
  const [uploading, setUploading] = useState(false)
  const [deleteTarget, setDeleteTarget] = useState(null)
  const [deleting, setDeleting] = useState(false)
  const fileInputRef = useRef(null)

  // Load model options from DB
  useEffect(() => {
    const loadModels = async () => {
      setModelsLoading(true)
      try {
        const categories = await fetchProductCategories()
        const modelCat = categories.find(c => c.name?.toLowerCase() === "model")
        if (modelCat) {
          const options = await fetchCategoryOptions(modelCat.id)
          setModels(options.map(opt => ({
            code: opt.option_value || opt.option_name,
            label: opt.description
              ? `${opt.option_name} - ${opt.description}`
              : opt.option_name,
          })))
        }
      } catch (err) {
        console.error("Error loading models:", err)
        onSnackbar?.({ open: true, message: t("Error loading models"), severity: "error" })
      } finally {
        setModelsLoading(false)
      }
    }
    loadModels()
  }, [onSnackbar, t])

  const fetchSwatches = useCallback(async (pg) => {
    if (!pg) return
    setLoading(true)
    try {
      const res = await fetch(`${API_BASE}/${pg}/?api_key=${API_KEY}&_t=${Date.now()}`, {
        headers: getHeaders(),
      })
      if (!res.ok) throw new Error(`HTTP ${res.status}`)
      const data = await res.json()
      const files = (data.resource || []).filter(f => f.type === "file")
      setSwatches(files)
    } catch (err) {
      console.error("Failed to fetch swatches:", err)
      setSwatches([])
      onSnackbar?.({ open: true, message: t("Failed to load swatches"), severity: "error" })
    } finally {
      setLoading(false)
    }
  }, [onSnackbar, t])

  const handlePGChange = (pg) => {
    setSelectedPG(pg)
    setSwatches([])
    if (pg) fetchSwatches(pg)
  }

  const handleUpload = async (e) => {
    const files = e.target.files
    if (!files || files.length === 0 || !selectedPG) return
    setUploading(true)
    let success = 0
    let fail = 0

    for (const file of files) {
      try {
        const res = await fetch(`${API_BASE}/${selectedPG}/${file.name}`, {
          method: "POST",
          headers: {
            ...getHeaders(),
            "Content-Type": file.type || "image/jpeg",
          },
          body: file,
        })
        if (res.ok) success++
        else fail++
      } catch {
        fail++
      }
    }

    setUploading(false)
    fileInputRef.current.value = ""

    if (success > 0) {
      onSnackbar?.({ open: true, message: t("Uploaded") + ` ${success} ` + t("swatches"), severity: "success" })
      fetchSwatches(selectedPG)
    }
    if (fail > 0) {
      onSnackbar?.({ open: true, message: `${fail} ` + t("uploads failed"), severity: "error" })
    }
  }

  const handleDelete = async () => {
    if (!deleteTarget || !selectedPG) return
    setDeleting(true)
    try {
      const res = await fetch(`${API_BASE}/${selectedPG}/${deleteTarget.name}?api_key=${API_KEY}`, {
        method: "DELETE",
        headers: getHeaders(),
      })
      if (!res.ok) throw new Error(`HTTP ${res.status}`)
      onSnackbar?.({ open: true, message: t("Swatch deleted"), severity: "success" })
      fetchSwatches(selectedPG)
    } catch (err) {
      console.error("Failed to delete swatch:", err)
      onSnackbar?.({ open: true, message: t("Failed to delete swatch"), severity: "error" })
    } finally {
      setDeleting(false)
      setDeleteTarget(null)
    }
  }

  const getSwatchUrl = (name) =>
    `${API_BASE}/${selectedPG}/${name}?api_key=${API_KEY}&v=2`

  const getLabel = (name) =>
    name.replace(/\.[^.]+$/, "").replace(/-/g, " ")

  return (
    <Box>
      {/* Header */}
      <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 2 }}>
        <Stack direction="row" spacing={2} alignItems="center">
          <Avatar sx={{ width: 40, height: 40, bgcolor: "#dbeafe", color: "#3b82f6" }}>
            <PaletteIcon sx={{ fontSize: 20 }} />
          </Avatar>
          <Box>
            <Typography variant="subtitle1" fontWeight={600} color="#111827">
              {t("Category Swatches")}
            </Typography>
            <Typography variant="body2" color="#6b7280">
              {t("Manage color swatches for each model")}
            </Typography>
          </Box>
        </Stack>
      </Stack>

      {/* Price group selector + upload */}
      <Stack direction="row" spacing={2} alignItems="center" sx={{ mb: 2 }}>
        <FormControl size="small" sx={{ minWidth: 300 }}>
          <Select
            value={selectedPG}
            onChange={(e) => handlePGChange(e.target.value)}
            displayEmpty
            sx={{ fontSize: "0.85rem" }}
          >
            <MenuItem value="">
              <em>{t("Select model...")}</em>
            </MenuItem>
            {modelsLoading ? (
              <MenuItem disabled>
                <CircularProgress size={16} sx={{ mr: 1 }} /> {t("Loading...")}
              </MenuItem>
            ) : (
              models.map((pg) => (
                <MenuItem key={pg.code} value={pg.code} sx={{ fontSize: "0.85rem" }}>
                  {pg.label}
                </MenuItem>
              ))
            )}
          </Select>
        </FormControl>

        {selectedPG && (
          <>
            <input
              ref={fileInputRef}
              type="file"
              accept="image/*"
              multiple
              style={{ display: "none" }}
              onChange={handleUpload}
            />
            <Button
              variant="contained"
              startIcon={uploading ? <CircularProgress size={16} color="inherit" /> : <CloudUploadIcon />}
              onClick={() => fileInputRef.current?.click()}
              disabled={uploading}
              sx={{
                bgcolor: "#3b82f6",
                "&:hover": { bgcolor: "#2563eb" },
                borderRadius: 2,
                textTransform: "none",
              }}
            >
              {uploading ? t("Uploading...") : t("Upload Swatches")}
            </Button>
          </>
        )}

        {selectedPG && !loading && (
          <Typography variant="body2" color="#64748b">
            {swatches.length} {t("swatches")}
          </Typography>
        )}
      </Stack>

      {/* Shared-family hint: lets admins know which other codes pick up these uploads */}
      {selectedPG && (() => {
        const famId = getColorFamily(selectedPG)
        if (!famId) return null
        const fam = COLOR_FAMILIES[famId]
        const isCanonical = fam.canonical === selectedPG
        const otherCodes = [...fam.boxes, ...fam.specials].filter(c => c !== selectedPG)
        if (otherCodes.length === 0) return null
        return (
          <Box
            sx={{
              mb: 2,
              p: 1.25,
              borderRadius: 1.5,
              bgcolor: isCanonical ? "#ecfdf5" : "#fef3c7",
              border: `1px solid ${isCanonical ? "#a7f3d0" : "#fde68a"}`,
            }}
          >
            <Typography variant="body2" sx={{ fontSize: "0.78rem", color: "#374151" }}>
              {isCanonical
                ? `${t("Uploads here are shared with")}: ${otherCodes.join(", ")} (${t("same color family")})`
                : `${t("This model inherits colors from")} ${fam.canonical}. ${t("Upload swatches to")} ${fam.canonical} ${t("to update this set.")}`}
            </Typography>
          </Box>
        )
      })()}

      {/* Swatch grid */}
      {loading ? (
        <Box sx={{ display: "flex", justifyContent: "center", py: 6 }}>
          <CircularProgress size={32} />
        </Box>
      ) : !selectedPG ? (
        <Box sx={{ textAlign: "center", py: 6, color: "#94a3b8" }}>
          <PaletteIcon sx={{ fontSize: 48, mb: 1, opacity: 0.4 }} />
          <Typography variant="body2">{t("Select a model to manage its swatches")}</Typography>
        </Box>
      ) : swatches.length === 0 ? (
        <Box sx={{ textAlign: "center", py: 6, color: "#94a3b8" }}>
          <Typography variant="body2">{t("No swatches found for this model")}</Typography>
        </Box>
      ) : (
        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: "repeat(auto-fill, minmax(100px, 1fr))",
            gap: 1.5,
          }}
        >
          {swatches.map((sw) => (
            <Box
              key={sw.name}
              sx={{
                position: "relative",
                border: "1px solid #e2e8f0",
                borderRadius: 1.5,
                overflow: "hidden",
                bgcolor: "#fff",
                transition: "all 0.15s",
                "&:hover": {
                  borderColor: "#3b82f6",
                  boxShadow: "0 2px 8px rgba(59,130,246,0.12)",
                  "& .delete-btn": { opacity: 1 },
                },
              }}
            >
              <Box
                component="img"
                src={getSwatchUrl(sw.name)}
                alt={getLabel(sw.name)}
                sx={{
                  width: "100%",
                  height: 80,
                  objectFit: "cover",
                  display: "block",
                  bgcolor: "#f1f5f9",
                }}
                onError={(e) => {
                  e.target.style.display = "none"
                }}
              />
              <Box sx={{ px: 0.75, py: 0.5 }}>
                <Typography
                  variant="caption"
                  sx={{
                    fontSize: "0.6rem",
                    color: "#475569",
                    fontWeight: 500,
                    display: "block",
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                    whiteSpace: "nowrap",
                  }}
                >
                  {getLabel(sw.name)}
                </Typography>
              </Box>
              <Tooltip title={t("Delete")} arrow>
                <IconButton
                  className="delete-btn"
                  size="small"
                  onClick={() => setDeleteTarget(sw)}
                  sx={{
                    position: "absolute",
                    top: 2,
                    right: 2,
                    bgcolor: "rgba(239,68,68,0.85)",
                    color: "#fff",
                    opacity: 0,
                    transition: "opacity 0.15s",
                    width: 22,
                    height: 22,
                    "&:hover": { bgcolor: "#dc2626" },
                  }}
                >
                  <DeleteIcon sx={{ fontSize: 14 }} />
                </IconButton>
              </Tooltip>
            </Box>
          ))}
        </Box>
      )}

      {/* Delete confirmation dialog */}
      <Dialog open={!!deleteTarget} onClose={() => !deleting && setDeleteTarget(null)}>
        <DialogTitle>{t("Delete Swatch")}</DialogTitle>
        <DialogContent>
          <Typography>
            {t("Are you sure you want to delete")} <strong>{deleteTarget && getLabel(deleteTarget.name)}</strong>?
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteTarget(null)} disabled={deleting}>
            {t("Cancel")}
          </Button>
          <Button
            onClick={handleDelete}
            color="error"
            variant="contained"
            disabled={deleting}
            startIcon={deleting ? <CircularProgress size={16} color="inherit" /> : null}
          >
            {deleting ? t("Deleting...") : t("Delete")}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  )
}

export default SwatchesTab
